import React from 'react';
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Stack,
  Button,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
// import { isAuthenticated } from '../utils/storage';
import { WidgetConfig } from '../types';

export interface Vehicle {
  id: string;
  title?: string;
  brand?: string;
  model?: string;
  price?: string | number;
  mileage?: string | number;
  exterior?: string;
  vinNumber?: string;
  imageUrl?: string;
  carUrl?: string;
  propertyType?: string;
  listingId?: string;
}

interface MilesCarouselProps {
  vehicles: Vehicle[];
  totalVehicles: number;
  isVehicleLoading: boolean;
  onVehicleClick?: (vehicleId: string, vehicleUrl?: string, currentVehicle?: Vehicle) => void;
  onLikeVehicleClick?: (vehicleId: string, vehicleUrl?: string) => void;
  onLoadMoreVehicles?: () => void;
  onEnhanceClick: (currentVehicle?: Vehicle) => void;
  config: WidgetConfig;
}

function MilesCarousel({ vehicles, totalVehicles, isVehicleLoading, onVehicleClick, onLikeVehicleClick, onLoadMoreVehicles, onEnhanceClick, config }: MilesCarouselProps) {
  const [currentIndex, setCurrentIndex] = React.useState(0);

  if (!vehicles || vehicles.length === 0) {
    return null;
  }

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? vehicles.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === vehicles.length - 1 ? 0 : prev + 1));

    if (currentIndex === vehicles.length - 2) {
      onLoadMoreVehicles && onLoadMoreVehicles();
    }
  };

  const currentVehicle = vehicles[currentIndex];

  const formatPrice = (price?: string | number) => {
    if (price === undefined || price === null || price === '') return 'Contact for price';
    // const numPrice = typeof price === 'number' ? price : parseFloat(price);
    // if (Number.isNaN(numPrice)) return 'Contact for price';
    // return `$${numPrice.toLocaleString()}`;
    return price.toLocaleString();
  };

  const formatArea = (mileage?: string | number) => {
    if (mileage === undefined || mileage === null || mileage === '') return 'N/A';
    const numArea = typeof mileage === 'number' ? mileage : parseFloat(mileage);
    if (Number.isNaN(numArea)) return String(mileage);
    return `${numArea.toLocaleString()} sq ft`;
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 400,
        mx: 'auto',
        my: 2,
      }}
    >
      <Card
        sx={{
          bgcolor: '#EEF5FD',
          transition: 'transform 0.2s',
          '&:hover': onVehicleClick
            ? {
              transform: 'scale(1.02)',
            }
            : {},
          cursor: onVehicleClick ? 'pointer' : 'default',
        }}
        onClick={() => {
          onVehicleClick && currentVehicle && onVehicleClick(currentVehicle.id, currentVehicle.carUrl, currentVehicle);
        }}
      >
        <Box sx={{ position: 'relative', paddingTop: '56.25%', bgcolor: '#f5f5f5' }}>
          {currentVehicle.imageUrl ? (
            <CardMedia
              component="img"
              image={currentVehicle.imageUrl}
              alt={currentVehicle.title || `${currentVehicle.brand ?? ''} ${currentVehicle.model ?? ''}`.trim()}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <CardMedia
              component="img"
              // image={`/static/home_placeholder.png`}
              image={`${config?.webUrl}/static/home_placeholder.png`}
              alt="Property placeholder"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}

          {vehicles.length > 1 && (
            <>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevious();
                }}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.7)',
                  },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>

              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                disabled={isVehicleLoading}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.7)',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(0,0,0,0.3)',
                    color: 'rgba(255,255,255,0.5)',
                  },
                }}
              >
                {isVehicleLoading ? <CircularProgress size={20} color="inherit" /> : <ChevronRightIcon />}
              </IconButton>
            </>
          )}

          <Chip
            label={
              isVehicleLoading && currentIndex === vehicles.length - 1
                ? 'Loading...'
                : `${currentIndex + 1}/${totalVehicles > vehicles.length ? `${vehicles.length}+` : vehicles.length}`
            }
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              // bgcolor: 'rgba(0,0,0,0.65)',
              bgcolor: '#324881',
              color: 'white',
            }}
          />
        </Box>

        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            {currentVehicle.title || `${currentVehicle.brand ?? ''} ${currentVehicle.model ?? ''}`.trim() || 'Property'}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
            <Chip label={formatPrice(currentVehicle.price)} size="small" />
            <Chip label={formatArea(currentVehicle.mileage)} size="small" />
            {currentVehicle.exterior && <Chip label={currentVehicle.exterior} size="small" />}
            {currentVehicle.vinNumber && <Chip label={`Listing: ${currentVehicle.vinNumber}`} size="small" />}
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                onLikeVehicleClick && onLikeVehicleClick(currentVehicle.id, currentVehicle.carUrl,);
              }}
            >Ask a Question
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                onVehicleClick && onVehicleClick(currentVehicle.id, currentVehicle.carUrl, currentVehicle);
              }}
            >
              View Details
            </Button>


          </Stack>

          {/* <Stack direction="row" spacing={2} mt={1}>
          <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                onLikeVehicleClick && onLikeVehicleClick(currentVehicle.id);
                if (isAuthenticated()) {
                  window.open(
                    `${config?.webUrl}?dealerInfoId=${config?.dealerId}&hostURL=${window.location.href}`,
                    '_blank',
                    'noopener,noreferrer'
                  );
                } else if(onEnhanceClick) {
                  onEnhanceClick(currentVehicle);
                }
              }}
            >
              Enhance Experience 
            </Button>
           </Stack> */}
        </CardContent>
      </Card>

      {/* Skeleton Loading Card - shown when loading more vehicles */}
      {isVehicleLoading && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Card sx={{ width: '100%', opacity: 0.7 }}>
            <Skeleton
              variant="rectangular"
              sx={{
                paddingTop: '56.25%',
                bgcolor: 'rgba(0, 0, 0, 0.08)',
              }}
            />
            <CardContent sx={{ p: 2 }}>
              <Skeleton variant="text" width="80%" height={28} sx={{ mb: 1 }} />
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <Skeleton variant="rounded" width={80} height={24} />
                <Skeleton variant="rounded" width={70} height={24} />
                <Skeleton variant="rounded" width={90} height={24} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Skeleton variant="rounded" width="50%" height={36} />
                <Skeleton variant="rounded" width="50%" height={36} />
              </Stack>
            </CardContent>
          </Card>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.5,
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading more properties...
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default MilesCarousel;
